import { useMemo } from "react";
import { TooltipView } from "../../../../components/Tooltip";
import { PRAXIS_PLAY_EVENTS } from "../../../../integrations/posthog/events";
import { useAnalytics } from "../../../../integrations/posthog/useAnalytics";
import { useApp } from "../../../../services/app-context";
import { CallFlowStatusSelect } from "./CallFlowStatusSelect";
import { CallFlowQuestionView } from "./CallFlowQuestionView";
import { ReactComponent as ChevronLeftIcon } from "../../../../assets/icons/chevron-left.svg";
import { getFinalStatusOnVerdict } from "./CertificationProfileView";
import { getReview } from "../../utils/review";
import { twMerge } from "tailwind-merge";
import { isAIRecommendationEnabled, isCertificationOverallSummaryEnabled } from "../../../../integrations/posthog/featureFlags";
import { Category } from "../../types";

const getCallFlowLevelEvaluation = (certificationAssets: any) => {
    const features = certificationAssets?.features || {};
    return features?.callFlowLevelEvaluation ?? true;
}

const getVerdictBgColor = (verdict: boolean, attemptInfo: any, metric: string, certificationAssets: any, allowAiRecommendation: boolean, colorType: "backgroundColor" | "textColor" = "backgroundColor") => {
    let status = "pending";
    if (verdict !== undefined) {
        status = verdict === true ? "passed" : verdict === false ? "failed" : "pending";
    } else if (allowAiRecommendation) {
        let aiVerdict = attemptInfo?.aiReview?.rubric?.[metric]?.verdict;
        status = aiVerdict === true ? "passed" : aiVerdict === false ? "failed" : "pending";
    }
    return certificationAssets?.userStatus?.[status]?.[colorType];
}

/** Format summary key for display (e.g. "actionItems" -> "Action Items") */
export function formatSummaryKey(key: string): string {
    return key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
}

/** Display order for summary sections below feedback */
export const SUMMARY_DISPLAY_ORDER = ["whatWentWell", "whatCouldImprove", "howToImprove", "actionItems"];


function CallFlow({ selectedCertificationAttempt, activeTab: activeTabIndex, form, setForm, setActiveTab, detailedReport, assignmentDetails, attemptInfo, allowStatusChange }: { selectedCertificationAttempt: any, activeTab: number | string, form: any, setForm: (form: any) => void, setActiveTab: (tab: number | string) => void, detailedReport: any, assignmentDetails: any, attemptInfo: any, allowStatusChange: boolean }) {
    const { captureEvent } = useAnalytics();
    const { tenantProfile } = useApp();
    const info_badges = tenantProfile?.info_badges || {};
    const { certificationAssets } = useApp();
    const callFlowLevelEvaluation = getCallFlowLevelEvaluation(certificationAssets);


    const assignmentCategories: Category[] = useMemo(() => {
        return assignmentDetails?.categories || attemptInfo?.categories || [];
    }, [assignmentDetails?.categories, attemptInfo?.categories]);

    const questions = useMemo(() => {
        return assignmentDetails?.questions || attemptInfo?.questions || [];
    }, [attemptInfo?.questions, assignmentDetails?.questions]);


    const activeTabCategory = assignmentCategories[activeTabIndex as number];
    const activeTab = activeTabCategory?.key;
    const activeTabLabel = activeTabCategory?.label || activeTab;

    const onMetricClick = (metric: string, metricIndex: number) => {
        setActiveTab(metricIndex)
        captureEvent(PRAXIS_PLAY_EVENTS.DETAILED_REPORT_METRIC_SELECTED, {
            metric_type: metric,
            metric_index: metricIndex,
            session_id: detailedReport?.session?.sessionID,
        })
    }

    const isManualReview = selectedCertificationAttempt === "manual";

    // Each insight highlights what impacted your Praxis Score and where you can improve.
    const answersByQuestion = attemptInfo?.answersByQuestion || [];
    const selectedCategoryIndex = activeTabIndex as number;
    const aiReview = attemptInfo?.aiReview?.rubric?.[activeTab];
    const aiOutputs = aiReview?.aiOutputs;
    const feedback = aiOutputs?.feedback;
    const summaries = aiOutputs?.summaries;
    const aiStatus = getFinalStatusOnVerdict(aiReview?.verdict, certificationAssets);
    const review = getReview(attemptInfo, selectedCertificationAttempt);
    const isReviewed = [true, false].includes(review?.[activeTabIndex]?.verdict);
    const showAIRecommendation = isAIRecommendationEnabled();
    const showOverallSummary = isCertificationOverallSummaryEnabled();
    return (
        <div className="flex gap-2 w-full rounded-[10px] py-3">
            <div className="flex flex-col border-[0.5px] border-[#D4D4D8] rounded-[10px]">
                <div className="flex items-center border-b border-[#DFDFDF] gap-2 px-3.5 py-3 justify-between">
                    <span className="text-sm font-medium uppercase">Call Flow</span>
                    {!!info_badges?.praxispro_callflow_insights && (<TooltipView content={info_badges?.praxispro_callflow_insights} id="callflow-insights" className="!z-10" />)}
                </div>
                <div className={twMerge("flex flex-col pb-4 overflow-y-auto", showOverallSummary ? "max-h-[calc(100vh-350px)]" : "max-h-[calc(100vh-300px)]")}>
                    {assignmentCategories?.map((metric, index) => (
                        <div
                            style={(callFlowLevelEvaluation ? {
                                borderLeft: activeTabIndex === index ? `2px solid ${getVerdictBgColor(form.rubric[index]?.verdict, attemptInfo, metric.key, certificationAssets, showAIRecommendation, "textColor")}` : "2px solid transparent",
                                backgroundColor: activeTabIndex === index ? getVerdictBgColor(form.rubric[index]?.verdict, attemptInfo, metric.key, certificationAssets, showAIRecommendation, "backgroundColor") : "transparent"
                            } : {})}
                            className={twMerge("flex items-center border-b-[0.5px] border-[#D4D4D8]", !callFlowLevelEvaluation && activeTabIndex === index ? "border-l-[2px] border-l-primary bg-gray-100" : "")}
                            key={metric.key}>
                            <div
                                onClick={() => {
                                    onMetricClick(metric.key, index)
                                }} className={`cursor-pointer py-3.5 pl-3 pr-3.5 w-[300px]  flex flex-col justify-center items-center gap-[5px]`}>
                                <div
                                    className={`self-stretch flex justify-between  ${activeTabIndex === index ? "items-start" : "items-center"}`}>
                                    <div>
                                        <div className="flex items-center gap-1.5">
                                            {callFlowLevelEvaluation && <div className="w-[5px] h-[5px] rounded-full" style={{ backgroundColor: getVerdictBgColor(form.rubric[index]?.verdict, attemptInfo, metric.key, certificationAssets, showAIRecommendation, "textColor") }} />}
                                            <div className="justify-start text-[#1E1E1E] text-[12px] font-normal leading-snug">{metric.label || metric.key}</div>
                                        </div>
                                        {
                                            activeTabIndex === index && (
                                                <div className="[&_*]:!text-[12px] remove-review-list-style " dangerouslySetInnerHTML={{ __html: "" }} />
                                            )
                                        }
                                    </div>
                                    <div>
                                        <ChevronLeftIcon className={`w-[18px] h-[18px] ${activeTabIndex === index ? "rotate-180" : "rotate-[270deg]"}`} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                </div>
            </div>
            {activeTabIndex !== "" &&
                <>
                    <div className="border border-[#DFDFDF] rounded-[10px] flex-1">
                        <div
                            style={(callFlowLevelEvaluation ? {
                                backgroundColor: getVerdictBgColor(form.rubric[activeTabIndex]?.verdict, attemptInfo, activeTab, certificationAssets, showAIRecommendation, "backgroundColor")
                            } : {})}
                            className={`flex flex-col gap-2 justify-center rounded-t-[10px] border-b border-[#DFDFDF]`} >
                            <div className={`flex justify-between items-center px-2.5 py-2.5`}>
                                <h3 className="text-[clamp(11px,1.5vw,15px)] text-[#464646] uppercase font-semibold tracking-[-0.14px]">{activeTabLabel}</h3>
                                {callFlowLevelEvaluation && <CallFlowStatusSelect
                                    certificationAssets={certificationAssets}
                                    value={form.rubric[activeTabIndex]?.verdict === true ? "passed" : form.rubric[activeTabIndex]?.verdict === false ? "failed" : "select"}
                                    onChange={(value) => {
                                        setForm((_form: any) => {
                                            const rubric = _form.rubric || [];
                                            rubric[activeTabIndex] = { verdict: value === "passed" ? true : false };
                                            return { ..._form, rubric };
                                        })
                                    }}
                                    readonly={!allowStatusChange || isReviewed}
                                />}

                            </div>
                        </div>
                        {showAIRecommendation && <div className="text-sm font-medium leading-[15px] px-3 my-2">
                            <span className="text-black">AI Recommendation:</span>
                            <span className="text-text2 text-black text-xs font-semibold leading-3 ml-1" style={{ color: aiStatus?.textColor }}>{isManualReview ? "N/A" : aiStatus?.label || "N/A"}</span>
                        </div>}

                        <div className={`relative bg-white`}>

                            <div className={twMerge("bg-white rounded-[10px] py-3 overflow-y-auto", showAIRecommendation ? "max-h-[calc(100vh-450px)]" : "max-h-[calc(100vh-400px)]")}>
                                <div className="pb-4 text-sm">
                                    {isManualReview ? (
                                        <div className="px-2.5">No Attempt Recorded</div>
                                    ) : (
                                        <>
                                            <div className="border-b px-2.5 border-[#DFDFDF] pb-2.5">
                                                <div className="!text-[clamp(10px,1.5vw,14px)] font-semibold mb-1.5 uppercase text-headerPrimary">Summary</div>
                                                <div className="!text-[clamp(10px,1.5vw,14px)] text-black">
                                                    {feedback || "No Feedback Recorded"}
                                                </div>
                                            </div>
                                            {summaries && Object.keys(summaries).length > 0 && (
                                                <div className="mt-4">
                                                    {(() => {
                                                        const orderedKeys = [
                                                            ...SUMMARY_DISPLAY_ORDER.filter((k) => k in summaries),
                                                            ...Object.keys(summaries).filter((k) => !SUMMARY_DISPLAY_ORDER.includes(k)),
                                                        ];
                                                        return orderedKeys.map((key, index) => {
                                                            const items = summaries[key];
                                                            const list = Array.isArray(items) ? items : [];
                                                            if (list.length === 0) return null;
                                                            return (
                                                                <div key={key} className={twMerge("px-2.5  pb-2.5", index !== orderedKeys.length - 1 ? "border-b border-[#DFDFDF]" : "")}>
                                                                    <div className="!text-[clamp(10px,1.5vw,14px)] font-semibold mb-1.5 uppercase text-headerPrimary my-3">{formatSummaryKey(key)}</div>
                                                                    <ul className="list-disc space-y-2 ml-3.5">
                                                                        {list.map((item, i) => (
                                                                            <li key={i} className="!text-[clamp(10px,1.5vw,14px)]">{item}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            );
                                                        });
                                                    })()}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <CallFlowQuestionView
                        isManualReview={isManualReview}
                        index={selectedCategoryIndex}
                        audioUrl={answersByQuestion?.[selectedCategoryIndex] || ""}
                        question={questions?.[selectedCategoryIndex] || ""}
                        answer={aiReview?.accountSpecialist || aiReview?.salesRep || "No Response Recorded"}
                    />
                </>
            }
        </div >);
}

export default CallFlow;